/**
 * Firestore — Supplier Contract Review.
 *
 * Database   : default
 * Collection : /workflows/contract-review/projects/{reviewId}
 *
 * Lives on the generic /workflows/{workflowSlug}/projects/{projectId} path
 * (see firestore.rules) so owner-gating works out of the box:
 *   create/update/delete require request.auth != null and
 *   the doc's `created_by` field == the caller's uid.
 *
 * No rules change is required for v1 (owner-only reads). Public sharing would
 * extend the `allow read` condition in firestore.rules like `rfp-packs`.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import { getFirebaseFirestore } from "@/platform/firebase/client";
import { getUserProfile, setFreeContractUsed as markFreeContractUsed } from "@/platform/firebase/firestore/users";
import type { ContractReview, ContractReviewRecord } from "@/modes/agentic-workflows/contract-review/types";
import { FREE_REVIEW_LIMIT } from "@/modes/agentic-workflows/contract-review/types";

const WF_SLUG = "contract-review";
const COL = "projects";

function db() { return getFirebaseFirestore(); }
function projectRef(id: string) { return doc(db(), "workflows", WF_SLUG, COL, id); }
function projectCol() { return collection(db(), "workflows", WF_SLUG, COL); }

/** Recursively drop undefined values so setDoc/updateDoc never reject. */
function cleanForWrite<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => cleanForWrite(v)) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      out[k] = cleanForWrite(v);
    }
    return out as T;
  }
  return value;
}

export interface CreateReviewInput {
  review: ContractReview;
  client_id: string;
  client_name: string;
  source_text: string;
  source_name: string;
  /** Base64 of the original .docx so the redlined-contract export can reopen the true source. */
  source_docx_b64?: string;
}

export async function createReview(
  userId: string,
  userEmail: string,
  input: CreateReviewInput,
): Promise<ContractReviewRecord> {
  const id = `cr-${Date.now()}`;
  const now = new Date().toISOString();
  const record: ContractReviewRecord = {
    ...input.review,
    id,
    created_by: userId,
    created_by_email: userEmail,
    client_id: input.client_id,
    client_name: input.client_name,
    created_at: now,
    updated_at: now,
    source_text: input.source_text,
    source_name: input.source_name,
    ...(input.source_docx_b64 ? { source_docx_b64: input.source_docx_b64 } : {}),
  };
  await setDoc(projectRef(id), cleanForWrite(record));
  return record;
}

export async function getReview(id: string): Promise<ContractReviewRecord | null> {
  const snap = await getDoc(projectRef(id));
  return snap.exists() ? (snap.data() as ContractReviewRecord) : null;
}

export async function getUserReviews(userId: string): Promise<ContractReviewRecord[]> {
  const q = query(projectCol(), where("created_by", "==", userId), limit(100));
  const snap = await getDocs(q);
  const reviews = snap.docs.map((d) => d.data() as ContractReviewRecord);
  return reviews.sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""));
}

/** Count a user's reviews — used by the free-tier generation limit gate. */
export async function countUserReviews(userId: string): Promise<number> {
  const q = query(projectCol(), where("created_by", "==", userId));
  const snap = await getDocs(q);
  return snap.size;
}

/**
 * Authoritative free-tier gate — reads the persistent profile flag AND the
 * live review count, so deleting a review never restores the consumed
 * allowance (the allowance is life-long once the first review is made).
 */
export async function freeReviewAllowanceUsed(userId: string): Promise<boolean> {
  const profile = await getUserProfile(userId).catch(() => null);
  if (profile?.freeContractUsed) return true;
  return (await countUserReviews(userId)) >= FREE_REVIEW_LIMIT;
}

/** Set the persistent free-review-used marker on the user's profile. */
export async function setFreeContractUsed(userId: string): Promise<void> {
  await markFreeContractUsed(userId);
}

export async function updateReview(id: string, data: Partial<ContractReviewRecord>): Promise<void> {
  const clean = { ...data };
  delete (clean as Record<string, unknown>).id;
  await updateDoc(projectRef(id), {
    ...cleanForWrite(clean),
    updated_at: new Date().toISOString(),
  });
}

export async function deleteReview(id: string): Promise<void> {
  await deleteDoc(projectRef(id));
}
